'use client';

import React, { useState } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { Dialog, PulseButton } from 'src/components';
import { useAuth } from 'src/context/auth';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import useUserImpersonation from 'src/hooks/users/useUserImpersonation';
import {
  useGetAllCompaniesQuery,
  useGetCompanyByIdQuery,
  useDeleteCompanyMutation,
} from 'src/api/redux';

interface DeactivateCompanyDialogProps {
  open: boolean;
  onClose: () => void;
}

const DeactivateCompanyDialog = ({
  open,
  onClose,
}: DeactivateCompanyDialogProps) => {
  const router = useRouter();
  const { companyId } = useParams<{ companyId: string }>();
  const { user } = useAuth();
  const log = useLogging();
  const { setNotification } = useNotification();
  const { stopImpersonating } = useUserImpersonation();

  const { refetch: fetchallCompanies } = useGetAllCompaniesQuery({});
  const { data: currentCompany } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );
  const [deleteCompanyMutation] = useDeleteCompanyMutation();
  const isSupportUser = user?.permissions?.is_support_user;

  const [isLoading, setIsLoading] = useState(false);

  const handleDeactivate = async () => {
    if (!companyId) return;

    try {
      setIsLoading(true);
      await deleteCompanyMutation(companyId).unwrap();
      setNotification({
        title: 'Success',
        message: 'Company deactivated successfully',
        type: 'success',
      });
      router.push('/settings/company');
      if (isSupportUser) {
        stopImpersonating();
      }
    } catch (err: any) {
      log.exception('Failed to deactivate company', err);
      setNotification({
        title: 'Error',
        message:
          err?.data?.error_message ||
          err?.message ||
          'Failed to deactivate company',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      onClose();
      fetchallCompanies();
    }
  };

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      showCloseButton={true}
      aria-labelledby="deactivate-company-dialog-title"
      aria-describedby="deactivate-company-dialog-description"
      data-testid="deactivate-company-dialog"
    >
      <Dialog.Header>
        <div
          id="deactivate-company-dialog-title"
          className="text-lg font-medium text-neutral-600"
          data-testid="deactivate-company-dialog-header"
        >
          Deactivate Company
        </div>
      </Dialog.Header>
      <Dialog.Content>
        <div
          className="flex flex-col gap-2 p-2 h-full w-full"
          data-testid="deactivate-company-dialog-content"
        >
          <span
            id="deactivate-company-dialog-description"
            className="font-medium text-neutral-500 p-2"
            role="alert"
          >
            Are you sure you want to deactivate{' '}
            <strong>{currentCompany?.name}</strong>?
          </span>
        </div>
      </Dialog.Content>
      <Dialog.Actions>
        <div className="flex flex-row gap-2">
          <PulseButton
            variant="outline"
            color="neutral"
            onClick={onClose}
            data-testid="deactivate-company-dialog-cancel"
          >
            Cancel
          </PulseButton>
          <PulseButton
            variant="outline"
            color="danger"
            onClick={handleDeactivate}
            data-testid="deactivate-company-dialog-deactivate"
            aria-label={`Deactivate ${currentCompany?.name || 'company'}`}
            loading={isLoading}
          >
            Deactivate
          </PulseButton>
        </div>
      </Dialog.Actions>
    </Dialog>
  );
};

export default DeactivateCompanyDialog;
